import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {projectLookup} from '../../../redux/slices/lookup.slice'
import {activityReportGetAll, activityReportExcel} from '../../../redux/slices/workplan.slice'

import {ActivityReportType} from '../../../helpers'

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()
  const pageKey = 'ACTIVITYREPORT'

  const selectActivityReportData = useMemo(
    () => (state) => ({
      activityReport: state.workplan.activityReport,
      loading: state.workplan.loading
    }),
    []
  )

  const selectLookupData = useMemo(
    () => (state) => ({
      projectLookupList: state.lookup.projectLookupList
    }),
    []
  )

  const {activityReport, loading} = useSelector(selectActivityReportData)
  const {projectLookupList} = useSelector(selectLookupData)

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(activityReportGetAll(params))
  }, [params, dispatch])

  useEffect(() => {
    dispatch(projectLookup())
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        activityReportExcel({
          params,
          fileName: intl.formatMessage({id: 'BC.ACTIVITYREPORT'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const Columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'ACTIVITYREPORT.SEARCH.ACTIVITYREPORTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'activityReportName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'ACTIVITYREPORT.SEARCH.PROJECTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'ACTIVITYREPORT.SEARCH.EDITDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'editDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'ACTIVITYREPORT.SEARCH.PROJECTLEADER'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectLeader',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'ACTIVITYREPORT.SEARCH.TECHNICALOFFICECHIEF'})}
          className='min-w-125px'
        />
      ),
      accessor: 'technicalOfficeChief',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'ACTIVITYREPORT.SEARCH.ACTIVITYREPORTSTATENAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'activityReportStateName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemo(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'ACTIVITYREPORT.SEARCH.ACTIVITYREPORTNAME',
          data: ActivityReportType,
          name: 'activityReportType',
        },
        {
          title: 'ACTIVITYREPORT.SEARCH.PROJECTNAME',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'ACTIVITYREPORT.SEARCH.EDITDATE',
          type: 'date',
          name: 'reportDate',
        },
      ],
    },
  ], [handleExcelExport, ActivityReportType, projectLookupList])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.ACTIVITYREPORT'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!activityReport && <ListLoading />}
          {activityReport && (
            <Table
              items={activityReport}
              cols={Columns}
              handlePagination={handlePagination}
              pages={pageKey}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}

export {List}
